import { memo } from "react";
import { Activity, Target, FileText, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { ClientKPIData } from "@/services/clientKPIService";

interface ClientKPIBadgesProps {
  kpiData?: ClientKPIData;
  isLoading?: boolean;
}

const ClientKPIBadges = memo(({ kpiData, isLoading }: ClientKPIBadgesProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-4 gap-1 mt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-6 bg-muted/50 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!kpiData) {
    return null;
  }

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'gray': return 'bg-gray-100 text-gray-700';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'green': return 'bg-green-100 text-green-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getGradientClasses = (colors: string[]) => {
    if (colors.length === 1) {
      return getColorClasses(colors[0]);
    }
    
    // Create gradient for multiple colors
    const uniqueColors = [...new Set(colors)];
    if (uniqueColors.length === 1) {
      return getColorClasses(uniqueColors[0]);
    }
    
    // Multi-color gradient
    const gradientColors = uniqueColors.map(color => {
      switch (color) {
        case 'gray': return '#6b7280';
        case 'yellow': return '#eab308';
        case 'green': return '#22c55e';
        case 'red': return '#ef4444';
        default: return '#6b7280';
      }
    }).join(', ');
    
    return `text-white`;
  };

  const getGradientStyle = (colors: string[]) => {
    if (colors.length <= 1) return undefined;
    
    const uniqueColors = [...new Set(colors)];
    if (uniqueColors.length === 1) return undefined;
    
    const gradientColors = uniqueColors.map(color => {
      switch (color) {
        case 'gray': return '#6b7280';
        case 'yellow': return '#eab308';
        case 'green': return '#22c55e';
        case 'red': return '#ef4444';
        default: return '#6b7280';
      }
    });
    
    return {
      background: `linear-gradient(45deg, ${gradientColors.join(', ')})`
    };
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const getComparison = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? '+' : '';
    const change = ((current - previous) / previous) * 100;
    if (change > 0) return `+${change.toFixed(0)}%`;
    if (change < 0) return `${change.toFixed(0)}%`;
    return '';
  };

  return (
    <TooltipProvider>
      <div className="grid grid-cols-4 gap-1 mt-2">
        {/* Activities KPI */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={cn(
                "flex flex-col items-center justify-center px-1 py-1 rounded text-xs font-medium cursor-help",
                getColorClasses(kpiData.activities.color)
              )}
            >
              <Activity className="h-3 w-3 mb-0.5" />
              <span className="text-[10px] leading-none">{kpiData.activities.current}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Activities P6M: {kpiData.activities.current} current vs {kpiData.activities.previous} previous ({getComparison(kpiData.activities.current, kpiData.activities.previous)})</p>
          </TooltipContent>
        </Tooltip>

        {/* Initiatives KPI */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={cn(
                "flex flex-col items-center justify-center px-1 py-1 rounded text-xs font-medium cursor-help",
                getGradientClasses(kpiData.initiatives.colors)
              )}
              style={getGradientStyle(kpiData.initiatives.colors)}
            >
              <Target className="h-3 w-3 mb-0.5" />
              <span className="text-[10px] leading-none">{kpiData.initiatives.current}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Initiatives P6M: {kpiData.initiatives.current} current vs {kpiData.initiatives.previous} previous ({getComparison(kpiData.initiatives.current, kpiData.initiatives.previous)})</p>
          </TooltipContent>
        </Tooltip>

        {/* Proposals KPI */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={cn(
                "flex flex-col items-center justify-center px-1 py-1 rounded text-xs font-medium cursor-help",
                getGradientClasses(kpiData.proposals.colors)
              )}
              style={getGradientStyle(kpiData.proposals.colors)}
            >
              <FileText className="h-3 w-3 mb-0.5" />
              <span className="text-[10px] leading-none">{kpiData.proposals.current}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Proposals P6M: {kpiData.proposals.current} current vs {kpiData.proposals.previous} previous ({getComparison(kpiData.proposals.current, kpiData.proposals.previous)})</p>
          </TooltipContent>
        </Tooltip>

        {/* Business KPI */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={cn(
                "flex flex-col items-center justify-center px-1 py-1 rounded text-xs font-medium cursor-help",
                getColorClasses(kpiData.business.color)
              )}
            >
              <DollarSign className="h-3 w-3 mb-0.5" />
              <span className="text-[10px] leading-none">€{formatNumber(kpiData.business.current)}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Business YTD: €{formatNumber(kpiData.business.current)} current vs €{formatNumber(kpiData.business.previous)} previous ({getComparison(kpiData.business.current, kpiData.business.previous)})</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
});

ClientKPIBadges.displayName = "ClientKPIBadges";

export default ClientKPIBadges;